import { type FC } from 'react';

import { graphql, type ResultOf } from 'graphql-schema';
import { openMap } from '../../../utilities';
import { RentalModuleTypes } from '../../../constants';
import { RentalCard } from '../RentalCard';
import type { DiscoverRentalCardProps } from './types';

export const DISCOVER_RENTAL_SPORT_CARD_FRAGMENT = graphql(`
  fragment DiscoverRentalSportCard on rentals @_unmask {
    __typename
    _id
    capacity
    banner_message
    prime_price_cents
    standard_price_cents
    courts(order_by: { name: asc }) {
      _id
      name
    }
    images(limit: 1) {
      _id
      url
    }
    sportBySport {
      _id
      name
      surface_type
    }
    venueByVenue {
      _id
      latitude
      longitude
      formatted_address
      place_id
      shorthand_name
      image_url
      neighborhoodByNeighborhoodId {
        _id
        name
      }
    }
  }
`);

type Props = DiscoverRentalCardProps & {
  data: ResultOf<typeof DISCOVER_RENTAL_SPORT_CARD_FRAGMENT>;
};

/** Wrapped Rental component. Runs the graphql queries and populates the modules */

export const DiscoverRentalSportCard: FC<Props> = ({
  data,
  onCardPress,
  overrideBannerMessage,
}) => {
  const {
    _id: id,
    venueByVenue: {
      image_url: venueImageUrl,
      shorthand_name: venueName,
      formatted_address: venueAddress,
      neighborhoodByNeighborhoodId,
      place_id: placeId,
    },
    courts,
    images: [rentalImage],
    prime_price_cents: highPrice,
    standard_price_cents: lowPrice,
    capacity: guestCapacity,
    sportBySport: { name: rentalActivity, surface_type: surfaceType },
    banner_message: bannerMessage,
  } = data;

  const neighborhoodName = neighborhoodByNeighborhoodId?.name || '';

  const imageUrl = rentalImage?.url ?? venueImageUrl ?? '';

  return (
    <RentalCard
      id={id}
      imageUrl={imageUrl}
      onCardPress={onCardPress}
      rentalActivity={rentalActivity}
      bannerMessage={overrideBannerMessage ?? bannerMessage}
      neighborhoodVenueModule={{
        type: RentalModuleTypes.NEIGHBORHOOD_VENUE,
        neighborhood: neighborhoodName,
        venue: {
          venueName,
          onPressVenue: () => openMap({ venueAddress, placeId }),
        },
      }}
      pricingModule={{
        type: RentalModuleTypes.RENTAL_PRICING,
        lowPrice,
        highPrice,
      }}
      displayNameModule={{
        type: RentalModuleTypes.SPORT_DISPLAY_NAME,
        rentalActivity: {
          name: rentalActivity,
          value: rentalActivity,
          surface: surfaceType,
        },
        venueName,
      }}
      detailModule={{
        type: RentalModuleTypes.CAPACITY_COURTS_NUMBER,
        guestCapacity,
        surfaceType,
        availableCourts: courts.length,
      }}
    />
  );
};

export default DiscoverRentalSportCard;
